import React from "react";
import { Calendar, FileText, User, Building2 } from "lucide-react";
import { Badge } from '../ui';

const ExportCard = ({ export: exportItem, onClick }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    // Extract just the date portion (YYYY-MM-DD) to avoid timezone issues
    // Works for both "YYYY-MM-DD" and "YYYY-MM-DDTHH:MM:SS.sssZ" formats
    const dateOnly = dateString.substring(0, 10);

    if (dateOnly.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateOnly.split('-');
      return new Date(year, month - 1, day).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }

    // Fallback if format is unexpected
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case "sent":
        return "success";
      case "pending":
        return "warning";
      case "draft":
        return "neutral";
      default:
        return "neutral";
    }
  };

  const getDesignTypes = (exportItem) => {
    const designTypes = [];

    if (exportItem.design_finish_grade) designTypes.push("Finish Grade");
    if (exportItem.design_rough_grade) designTypes.push("Rough Grade");
    if (exportItem.design_erosion_phase_1) designTypes.push("Erosion Control Phase 1");
    if (exportItem.design_erosion_phase_2) designTypes.push("Erosion Control Phase 2");
    if (exportItem.design_bottom_of_storm) designTypes.push("Bottom of Storm");
    if (exportItem.design_bottom_of_water) designTypes.push("Bottom of Water");
    if (exportItem.design_bottom_of_sanitary) designTypes.push("Bottom of Sanitary");
    if (exportItem.design_other && exportItem.design_other_name) {
      designTypes.push(exportItem.design_other_name);
    } else if (exportItem.design_other) {
      designTypes.push("Other");
    }

    return designTypes;
  };

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--dark-elevated)',
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        cursor: 'pointer',
        transition: 'all var(--transition-fast)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--gray-500)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--gray-600)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Card Header */}
      <div
        style={{
          marginBottom: 'var(--space-3)',
          paddingBottom: 'var(--space-3)',
          borderBottom: '1px solid var(--gray-700)',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--gray-50)',
            margin: 0,
            marginBottom: 'var(--space-2)',
          }}
        >
          {exportItem.projectName || 'Untitled Project'}
        </h3>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          {exportItem.jobNumber && (
            <Badge variant="info" size="sm">
              {exportItem.jobNumber}
            </Badge>
          )}
          <Badge variant={getStatusVariant(exportItem.status)} size="sm">
            {exportItem.status || 'pending'}
          </Badge>
        </div>
      </div>

      {/* Card Body */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {/* Client Name */}
        {exportItem.clientName && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
            }}
          >
            <Building2
              size={16}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
                wordBreak: 'break-word',
              }}
            >
              {exportItem.clientName}
            </span>
          </div>
        )}

        {/* Date */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-3)',
          }}
        >
          <Calendar
            size={16}
            style={{
              flexShrink: 0,
              marginTop: '2px',
              color: 'var(--gray-400)',
            }}
          />
          <span
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-300)',
            }}
          >
            {formatDate(exportItem.export_date || exportItem.created_at)}
          </span>
        </div>

        {/* File Formats */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-3)',
          }}
        >
          <FileText
            size={16}
            style={{
              flexShrink: 0,
              marginTop: '2px',
              color: 'var(--gray-400)',
            }}
          />
          {exportItem.file_formats_needed && exportItem.file_formats_needed.length > 0 ? (
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
                wordBreak: 'break-word',
              }}
            >
              {exportItem.file_formats_needed.join(", ")}
            </span>
          ) : Object.entries(exportItem).filter(
              ([key, value]) =>
                key.endsWith("_files") &&
                value &&
                (Array.isArray(value)
                  ? value.length > 0
                  : Object.keys(value).length > 0),
            ).length > 0 ? (
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
              }}
            >
              {
                Object.entries(exportItem).filter(
                  ([key, value]) =>
                    key.endsWith("_files") &&
                    value &&
                    (Array.isArray(value)
                      ? value.length > 0
                      : Object.keys(value).length > 0),
                ).length
              }{" "}
              file types attached
            </span>
          ) : (
            (() => {
              const designTypes = getDesignTypes(exportItem);
              if (designTypes.length > 0) {
                return (
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--gray-300)',
                      wordBreak: 'break-word',
                    }}
                  >
                    {designTypes.join(", ")}
                  </span>
                );
              } else {
                return (
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--gray-500)',
                    }}
                  >
                    No design types selected
                  </span>
                );
              }
            })()
          )}
        </div>

        {/* Additional Contacts */}
        {exportItem.additional_contact_ids && exportItem.additional_contact_ids.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
            }}
          >
            <User
              size={16}
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: 'var(--gray-400)',
              }}
            />
            <span
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-300)',
              }}
            >
              {exportItem.additional_contact_ids.length} additional contact
              {exportItem.additional_contact_ids.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportCard;
